/**
 * ExitStrategyIntelligence — /v3/intel/exit-intel
 *
 * BP-029 workspace tab: 8-strategy exit comparison.
 *
 * Widgets (per BP-029 spec):
 *   1. Exit Comparison Matrix — 8 strategies ranked side-by-side
 *   2. Profit Forecast — highlighted per strategy
 *   3. Risk Gauge — visual per strategy
 *   4. AI Recommendation — Claude coach + recommendation reason
 *   5. Portfolio Impact — how this deal shifts the workspace mix
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Award, TrendingUp, ShieldCheck, Home,
  RefreshCcw, Hammer, KeyRound, Handshake, Wallet, Building,
  Timer, DollarSign, Percent, AlertTriangle, ArrowRight, Layers,
} from "lucide-react";

const STRATEGY_META = {
  wholesale:      { label: "Wholesale",      icon: Handshake, accent: "#0EA5E9" },
  wholetail:      { label: "Wholetail",      icon: RefreshCcw,accent: "#06B6D4" },
  fix_flip:       { label: "Fix & Flip",     icon: Hammer,    accent: "#EA580C" },
  rental:         { label: "Rental",         icon: Home,      accent: "#10B981" },
  brrrr:          { label: "BRRRR",          icon: Building,  accent: "#8B5CF6" },
  seller_finance: { label: "Seller Finance", icon: Wallet,    accent: "#F59E0B" },
  novation:       { label: "Novation",       icon: KeyRound,  accent: "#D946EF" },
  hold:           { label: "Hold",           icon: Layers,    accent: "#64748B" },
};

const RISK_META = {
  low:      { label: "Low",       color: "text-emerald-600", bg: "bg-emerald-500/10",   border: "border-emerald-500/30", ring: "#10B981" },
  moderate: { label: "Moderate",  color: "text-amber-600",   bg: "bg-amber-500/10",     border: "border-amber-500/30",   ring: "#F59E0B" },
  elevated: { label: "Elevated",  color: "text-orange-600",  bg: "bg-orange-500/10",    border: "border-orange-500/30",  ring: "#EA580C" },
  high:     { label: "High",      color: "text-red-600",     bg: "bg-red-500/10",       border: "border-red-500/30",     ring: "#DC2626" },
};

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD",
                                    maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;

// ─────────────────────────────── page ───────────────────────────────────

export default function ExitStrategyIntelligence() {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/v3/acquisition-intel/top-leads?limit=50");
        if (!cancelled && Array.isArray(data.items)) {
          setProperties(data.items.map((r) => ({
            id: r.property_id, label: r.property_address, heat: r.heat,
          })));
          if (data.items[0] && !propId) setPropId(data.items[0].property_id);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!propId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v3/exit-intel/${propId}`);
        if (!cancelled) setMatrix(data);
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) setMatrix(null);
        } else toast.error("Failed to load exit intelligence.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [propId]);

  const analyze = useCallback(async () => {
    if (!propId) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/v3/exit-intel/analyze", {
        property_id: propId, force: true,
      });
      setMatrix(data);
      toast.success(`Analyzed ${data.strategies?.length ?? 0} exit strategies.`);
    } catch { toast.error("Exit analysis failed."); }
    finally { setAnalyzing(false); }
  }, [propId]);

  const recommended = matrix?.strategies?.find((s) => s.strategy === matrix.recommended);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="exit-intel-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
            <TrendingUp className="w-3.5 h-3.5" /> BP-029 · Exit Playbook
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 tracking-tight">Exit Strategy Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">
            Eight exits, one recommendation. Every dollar and every day audited.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="exit-intel-property-picker"
            value={propId}
            onChange={(e) => setPropId(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm min-w-[280px] focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            {properties.length === 0 && <option value="">No properties yet</option>}
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} {p.heat ? `· ${p.heat.toUpperCase()}` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={analyze}
            disabled={!propId || analyzing}
            data-testid="exit-intel-analyze"
            className="inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 shadow disabled:opacity-60"
            style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}
          >
            {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Analyze Exits
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {!loading && !matrix && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center"
             data-testid="exit-intel-empty">
          <Sparkles className="w-8 h-8 text-emerald-500 mx-auto" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">No exit intelligence yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Pick a property above and click <b>Analyze Exits</b> to run all 8 strategies.
          </p>
        </div>
      )}

      {matrix && (
        <>
          {/* Property snapshot */}
          <PropertySnapshot matrix={matrix} />

          {/* Recommended + Portfolio Impact side by side */}
          <div className="grid lg:grid-cols-3 gap-4">
            {recommended && (
              <div className="lg:col-span-2">
                <RecommendedCard recommended={recommended} matrix={matrix} />
              </div>
            )}
            <PortfolioImpactCard impact={matrix.portfolio_impact || {}} />
          </div>

          {/* Exit Comparison Matrix */}
          <ExitComparisonMatrix strategies={matrix.strategies} recommended={matrix.recommended} />

          {/* Profit Forecast + Risk Gauge widgets */}
          <div className="grid lg:grid-cols-2 gap-4">
            <ProfitForecastCard strategies={matrix.strategies} recommended={matrix.recommended} />
            <RiskGaugeCard strategies={matrix.strategies} recommended={matrix.recommended} />
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────── snapshot ────────────────────────────────── */

function PropertySnapshot({ matrix }) {
  const items = [
    { label: "ARV",             value: fmtMoney(matrix.arv) },
    { label: "Rehab",           value: fmtMoney(matrix.rehab) },
    { label: "Purchase price",  value: fmtMoney(matrix.purchase_price) },
    { label: "Est. monthly rent", value: fmtMoney(matrix.monthly_rent) },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="exit-intel-snapshot">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">Property snapshot</div>
          <h2 className="text-lg font-bold text-slate-900">{matrix.property_address}</h2>
        </div>
        <span className="text-[11px] text-slate-400 uppercase tracking-widest">
          Generated {new Date(matrix.generated_at).toLocaleString()}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{it.label}</div>
            <div className="mt-1 text-lg font-bold text-slate-900 tabular-nums">{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── recommended card ────────────────────────── */

function RecommendedCard({ recommended, matrix }) {
  const meta = STRATEGY_META[recommended.strategy];
  const Icon = meta.icon;
  const m = recommended.math;
  return (
    <div className="rounded-2xl border-2 border-emerald-300/60 bg-gradient-to-br from-emerald-50 via-white to-white p-6 shadow-md h-full"
         data-testid="exit-intel-recommended">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
              style={{ backgroundImage: "linear-gradient(90deg, #10B981, #059669)" }}>
          <Award className="w-3 h-3" /> AI Recommended Exit
        </span>
        <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">
          {matrix.ai_source === "claude" ? "Claude" : "Deterministic"}
        </span>
      </div>
      <div className="mt-3 flex items-start gap-4 flex-wrap">
        <div className="grid place-items-center h-14 w-14 rounded-xl shadow"
             style={{ background: `linear-gradient(135deg, ${meta.accent}, #059669)` }}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Recommended exit</div>
          <h2 className="text-2xl font-bold text-slate-900">{recommended.label}</h2>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            {matrix.recommendation_reason}
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniStat label="Profit"       value={fmtMoney(m.total_profit)} highlight />
        <MiniStat label="Annualized"   value={fmtPct(m.annualized_roi)} />
        <MiniStat label="Timeline"     value={`${m.timeline_days}d`} />
        <MiniStat label="Cash at close" value={fmtMoney(m.cash_at_close)} />
      </div>
      {matrix.ai_summary && (
        <div className="mt-4 rounded-xl bg-white border border-emerald-200 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-line"
             data-testid="exit-intel-ai-summary">
          {matrix.ai_summary}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg px-3 py-2 border ${highlight ? "border-emerald-200 bg-emerald-50/70" : "border-slate-100 bg-slate-50"}`}>
      <div className="text-[9.5px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

/* ─────────────────────────── portfolio impact ────────────────────────── */

function PortfolioImpactCard({ impact }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid="exit-intel-portfolio-impact">
      <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
        <Building className="w-3.5 h-3.5" /> Portfolio Impact
      </div>
      <div className="mt-4 space-y-3">
        <ImpactRow label="Units added"       value={impact.added_units || 0} />
        <ImpactRow label="Monthly cash flow" value={fmtMoney(impact.added_monthly_cf)} />
        <ImpactRow label="Equity added"      value={fmtMoney(impact.added_equity)} highlight />
      </div>
      {impact.concentration_note && (
        <p className="mt-3 text-[12px] leading-relaxed text-slate-600 italic border-t border-slate-100 pt-3">
          {impact.concentration_note}
        </p>
      )}
    </div>
  );
}

function ImpactRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-base font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

/* ─────────────────────────── comparison matrix ───────────────────────── */

function ExitComparisonMatrix({ strategies, recommended }) {
  return (
    <div className="space-y-3" data-testid="exit-intel-matrix">
      <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
        <TrendingUp className="w-3.5 h-3.5" /> Exit Comparison Matrix
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        {strategies.map((s) => (
          <StrategyCard key={s.strategy} strategy={s} isRec={s.strategy === recommended} />
        ))}
      </div>
    </div>
  );
}

function StrategyCard({ strategy, isRec }) {
  const meta = STRATEGY_META[strategy.strategy] || {};
  const risk = RISK_META[strategy.risk_band] || RISK_META.moderate;
  const Icon = meta.icon || Home;
  const m = strategy.math;
  return (
    <div className={`rounded-2xl border ${isRec ? "border-emerald-300 shadow-md" : "border-slate-200"} bg-white p-4`}
         data-testid={`exit-intel-strategy-${strategy.strategy}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="grid place-items-center h-8 w-8 rounded-lg text-white"
              style={{ background: `linear-gradient(135deg, ${meta.accent}, #059669)` }}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1">
          <div className="text-[10px] font-black tracking-widest uppercase text-slate-500">Strategy</div>
          <div className="text-sm font-bold text-slate-900 leading-tight">{strategy.label}</div>
        </div>
        {isRec && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-700 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
            <Award className="w-3 h-3" /> Rec
          </span>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        <MetricRow icon={DollarSign} label="Profit"     value={fmtMoney(m.total_profit)} bold />
        <MetricRow icon={Percent}    label="Annualized ROI" value={fmtPct(m.annualized_roi)} />
        <MetricRow icon={Timer}      label="Timeline"   value={`${m.timeline_days}d`} />
        {m.monthly_cash_flow !== 0 && (
          <MetricRow icon={DollarSign} label="Monthly CF" value={fmtMoney(m.monthly_cash_flow)} />
        )}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${risk.bg} ${risk.color} border ${risk.border}`}>
          <ShieldCheck className="w-3 h-3" /> {risk.label} risk
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Fit {strategy.fit_score}
        </span>
      </div>

      {strategy.warnings?.length > 0 && (
        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-[10.5px] text-amber-800 leading-snug">
            {strategy.warnings[0]}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricRow({ icon: Icon, label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <div className="flex items-center gap-1 text-slate-500">
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </div>
      <div className={`tabular-nums ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</div>
    </div>
  );
}

/* ─────────────────────────── profit forecast widget ──────────────────── */

function ProfitForecastCard({ strategies, recommended }) {
  const maxProfit = Math.max(...strategies.map((s) => s.math?.total_profit || 0), 1);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="exit-intel-forecast">
      <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
        <TrendingUp className="w-3.5 h-3.5" /> Profit Forecast
      </div>
      <div className="mt-4 space-y-2">
        {strategies.map((s) => {
          const profit = s.math?.total_profit || 0;
          const pct = maxProfit ? Math.max(0, (profit / maxProfit) * 100) : 0;
          const isRec = s.strategy === recommended;
          const meta = STRATEGY_META[s.strategy];
          return (
            <div key={s.strategy} className="flex items-center gap-3">
              <div className="w-32 text-[11px] font-semibold text-slate-700">{s.label}</div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full"
                     style={{ width: `${pct}%`,
                              background: isRec ? "linear-gradient(90deg, #10B981, #059669)" : meta.accent }} />
              </div>
              <div className={`w-24 text-right text-[12px] font-bold tabular-nums ${isRec ? "text-emerald-700" : "text-slate-700"}`}>
                {fmtMoney(profit)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── risk gauge widget ────────────────────────── */

function RiskGaugeCard({ strategies, recommended }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="exit-intel-risk">
      <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
        <ShieldCheck className="w-3.5 h-3.5" /> Risk Gauge
      </div>
      <div className="mt-4 space-y-2">
        {strategies.map((s) => {
          const risk = s.risk_score || 0;
          const meta = RISK_META[s.risk_band] || RISK_META.moderate;
          const isRec = s.strategy === recommended;
          return (
            <div key={s.strategy} className="flex items-center gap-3">
              <div className="w-32 text-[11px] font-semibold text-slate-700">{s.label}</div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full"
                     style={{ width: `${risk}%`, background: meta.ring }} />
              </div>
              <div className={`w-16 text-right text-[11px] font-bold uppercase tracking-widest ${meta.color}`}>
                {meta.label}
              </div>
              {isRec && (
                <ArrowRight className="w-3 h-3 text-emerald-600 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
